import { IWebApiRoot } from "../navdata";
import { NavAutoResolver } from "../services/autoresolveservice";
import { Subject } from "rxjs";

/** Compatibility for '$q', '$http' vs 'Promise', 'Http' */
export abstract class CompatibilityService implements IQService {
    constructor(protected readonly webApiRoot: IWebApiRoot = undefined) { }
    abstract reject<T = never>(reason: any): PromiseLike<T>;

    abstract when(): PromiseLike<void>;
    abstract when<T>(t: T | PromiseLike<T>): PromiseLike<T>;
    abstract when<T>(t?: T | PromiseLike<T>);

    abstract defer<T = any>(): IDeferred<T>;

    /** finally shorthand: execute after resolved, but keeping original result
     * (abstraction needed as 'finally' method available on ng.IPromise might not
     * be available on Promise Generalizations) */
    abstract finally<T>(p: PromiseLike<T>, action: Function): PromiseLike<T>;

    abstract all<TAll>(promises: Array<TAll | PromiseLike<TAll>>): PromiseLike<TAll[]>;
    abstract all<T>(promises: { [K in keyof T]: (PromiseLike<T[K]> | T[K]); }): PromiseLike<T>;

    abstract get<T>(url: string, config?: IRequestShortcutConfig): PromiseLike<IHttpResponse<T>>;

    abstract post<T>(url: string, data: any, config?: IRequestShortcutConfig): PromiseLike<IHttpResponse<T>>;

    /** clear cache on gets etc. */
    abstract clearCache();

    /** Parse Service Abstraction */
    abstract get parse(): IParseService;

    /** Timeout Service Abstraction */
    abstract get timeout(): ITimeoutService;

    /** Auto Resolve abstraction */
    abstract get resolve(): NavAutoResolver;

    /** external actions (outside angualar(js)) somtimes need explicit trigger (kendo, signalr, ...) */
    abstract tick(element?: Document | Node | JQuery): void;

    /** transfor Request url in case of custom api Root */
    protected url(url: string) {
        const urlTransform = this.webApiRoot && this.webApiRoot.WebApiRoot;
        if (urlTransform && url) {
            if (url[0] === "/") {
                url = urlTransform + url;
            }
        }
        return url;
    }

    public readonly Events = new Subject<{ Event: string, Args?: any; }>();
}

/** Cmopat ITimoutService */
export interface ITimeoutService {
    (delay?: number, invokeApply?: boolean): PromiseLike<void>;
}

/** compat IQService */
export interface IQService {
    when(): PromiseLike<void>;
    when<T>(t: T | PromiseLike<T>): PromiseLike<T>;
    when<T>(t?: T | PromiseLike<T>);
}

export interface IParseService {
    (expression: string): ((context: any, locals?: any) => any) /** ICompiledExpression **/;
}

/** Compat IDeferred */
export interface IDeferred<T> {
    resolve(value?: T | PromiseLike<T>): void;
    reject(reason?: any): void;
    promise: PromiseLike<T>;
}

/** Compat IHttpResponse  */
export interface IHttpResponse<T> {
    data: T;
    status: number;
    // headers: IHttpHeadersGetter;
    // config: IRequestConfig;
    // statusText: string;
    // /** Added in AngularJS 1.6.6 */
    // xhrStatus: 'complete' | 'error' | 'timeout' | 'abort';
}

/** Compat IRequestShortcutConfig  */
export interface IRequestShortcutConfig {
    params?: any;

    data?: any;

    cache?: boolean;
}
